import React from 'react';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Image,
    ImageFit,
    MessageBar,
    MessageBarType,
    PanelType,
    Pivot,
    PivotItem,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
    TooltipHost,
    useTheme,
} from '@fluentui/react';
import { toast } from 'react-toastify';
import PanelWrapper from '../../Common/PanelWrapper';
import { IStorefrontAsset, IStorefrontAssetUsage, STOREFRONT_ASSET_GLOBAL } from '../../../models/Storefront';
import {
    useDeleteStorefrontAssetMutation,
    useGetStorefrontAssetUsageQuery,
    usePromoteStorefrontAssetMutation,
    useReplaceStorefrontAssetMutation,
    useUpdateStorefrontAssetMutation,
} from '../../../store/endpoints/StorefrontAssetEndpoints';
import { useGetStorefrontsQuery } from '../../../store/endpoints/StorefrontSettingsEndpoints';
import AssetActivityList from './AssetActivityList';
import AssetTagInput from './AssetTagInput';
import {
    assetSourceLabel,
    blobPathTail,
    extractErrorMessage,
    formatBytes,
    usageLocationLabel,
} from './assetFormatting';

interface IAssetDetailsPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    asset?: IStorefrontAsset;
    // Friendly name for the owning storefront ('Global' is derived from the scope itself).
    storefrontName?: string;
}

type ConfirmKind = 'promote' | 'replace' | 'delete';
type TabKey = 'details' | 'references' | 'activity';

// The editor chrome the storefront editors use for a grouped block of settings.
const SETTINGS_BOX: React.CSSProperties = {
    padding: '1em',
    border: '1px solid #edebe9',
    borderRadius: 4,
    background: '#faf9f8',
};

const HELPER_TEXT: React.CSSProperties = { fontSize: 11, color: '#605e5c' };

// Small semibold divider label, matching the section/nav pod editors.
const FormSectionHeader: React.FC<{ label: string }> = (props) => (
    <div style={{ borderBottom: '1px solid #edebe9', paddingBottom: 2, marginBottom: '0.5em' }}>
        <Text variant='small' styles={{ root: { fontWeight: 600 } }}>{props.label}</Text>
    </div>
);

// The same pill the styles panel uses to flag org-wide scope.
const ScopePill: React.FC<{ label: string; global: boolean }> = (props) => (
    <span style={{
        fontSize: 10,
        padding: '1px 6px',
        borderRadius: 8,
        whiteSpace: 'nowrap',
        background: props.global ? '#e2e9cb' : '#f3f2f1',
        color: props.global ? '#43571a' : '#605e5c',
    }}>
        {props.label}
    </span>
);

const AssetDetailsPanel: React.FC<IAssetDetailsPanelProps> = (props) => {
    const theme = useTheme();
    const [updateAsset] = useUpdateStorefrontAssetMutation();
    const [promoteAsset] = usePromoteStorefrontAssetMutation();
    const [replaceAsset] = useReplaceStorefrontAssetMutation();
    const [deleteAsset] = useDeleteStorefrontAssetMutation();

    const [name, setName] = React.useState('');
    const [tags, setTags] = React.useState<string[]>([]);
    const [saving, setSaving] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
    const [confirm, setConfirm] = React.useState<ConfirmKind | undefined>();
    const [selectedTab, setSelectedTab] = React.useState<TabKey>('details');
    // The usage lookup is an org-wide scan server-side, so it stays unfetched until the References
    // tab is opened or the user reaches for Delete (which needs it as a guard).
    const [usageRequested, setUsageRequested] = React.useState(false);

    const replaceInputRef = React.useRef<HTMLInputElement | null>(null);

    const assetId = props.asset?.id;
    const usageEnabled = props.isOpen && !!assetId && usageRequested;
    const usage = useGetStorefrontAssetUsageQuery(assetId ?? '', {
        // References change from other screens, so re-check rather than trusting an earlier visit.
        skip: !usageEnabled,
        refetchOnMountOrArgChange: true,
    });
    const usages = React.useMemo(() => usage.data?.items ?? [], [usage.data]);
    const usageKnown = usageEnabled && !usage.isUninitialized && !usage.isFetching && usage.data !== undefined;

    const storefronts = useGetStorefrontsQuery(undefined, { skip: !props.isOpen });
    const storefrontNames = React.useMemo(() => {
        const map: { [id: string]: string } = {};
        (storefronts.data?.items ?? []).forEach((s) => { if (s.id) map[s.id] = s.name ?? s.id; });
        return map;
    }, [storefronts.data]);

    const isGlobal = props.asset?.storefront === STOREFRONT_ASSET_GLOBAL;
    const scopeLabel = isGlobal
        ? 'Global'
        : (props.storefrontName ?? (props.asset ? storefrontNames[props.asset.storefront] : undefined) ?? props.asset?.storefront ?? '');

    React.useEffect(() => {
        setName(props.asset?.name ?? '');
        setTags(props.asset?.tags ?? []);
        setErrorMessage(undefined);
        setConfirm(undefined);
    }, [props.asset]);

    // A different asset (or a fresh open) starts back on Details with the expensive lookup dropped.
    React.useEffect(() => {
        setSelectedTab('details');
        setUsageRequested(false);
    }, [assetId, props.isOpen]);

    const objectName = props.asset?.name || 'asset';

    const dirty = React.useMemo(() => {
        if (!props.asset) return false;
        const originalTags = props.asset.tags ?? [];
        if (name.trim() !== (props.asset.name ?? '')) return true;
        if (tags.length !== originalTags.length) return true;
        return tags.some((tag, i) => tag !== originalTags[i]);
    }, [props.asset, name, tags]);

    const openTab = (key: TabKey) => {
        setSelectedTab(key);
        if (key === 'references') setUsageRequested(true);
    };

    const save = async () => {
        if (!props.asset) return;
        setSaving(true);
        setErrorMessage(undefined);
        const toastId = toast.loading(`Saving ${objectName}…`);
        try {
            await updateAsset({ id: props.asset.id, name: name.trim(), tags }).unwrap();
            toast.update(toastId, { type: 'success', render: `${objectName} saved`, isLoading: false, autoClose: 3000 });
        } catch (e) {
            const detail = extractErrorMessage(e);
            setErrorMessage(detail ?? 'Failed to save asset');
            toast.update(toastId, {
                type: 'error',
                render: `Failed to save ${objectName}${detail ? `: ${detail}` : ''}`,
                isLoading: false,
                autoClose: 4000,
            });
        }
        setSaving(false);
    };

    const promote = async () => {
        if (!props.asset) return;
        setConfirm(undefined);
        setBusy(true);
        setErrorMessage(undefined);
        const toastId = toast.loading(`Promoting ${objectName}…`);
        try {
            await promoteAsset({ id: props.asset.id }).unwrap();
            toast.update(toastId, { type: 'success', render: `${objectName} promoted to Global`, isLoading: false, autoClose: 3000 });
        } catch (e) {
            const detail = extractErrorMessage(e);
            setErrorMessage(detail ?? 'Failed to promote asset');
            toast.update(toastId, {
                type: 'error',
                render: `Failed to promote ${objectName}${detail ? `: ${detail}` : ''}`,
                isLoading: false,
                autoClose: 4000,
            });
        }
        setBusy(false);
    };

    const onReplaceFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Let the same file be picked twice in a row.
        e.target.value = '';
        if (!file || !props.asset) return;
        if (file.size > 20971520) {
            setErrorMessage('File size exceeds 20 MB');
            return;
        }
        const asset = props.asset;
        const buffer = await file.arrayBuffer();
        setBusy(true);
        setErrorMessage(undefined);
        const toastId = toast.loading(`Replacing ${objectName}…`);
        try {
            await replaceAsset({ id: asset.id, file: buffer, fileName: file.name }).unwrap();
            toast.update(toastId, { type: 'success', render: `${objectName} replaced`, isLoading: false, autoClose: 3000 });
        } catch (err) {
            const detail = extractErrorMessage(err);
            setErrorMessage(detail ?? 'Failed to replace image');
            toast.update(toastId, {
                type: 'error',
                render: `Failed to replace ${objectName}${detail ? `: ${detail}` : ''}`,
                isLoading: false,
                autoClose: 4000,
            });
        }
        setBusy(false);
    };

    const remove = async () => {
        if (!props.asset) return;
        setConfirm(undefined);
        setBusy(true);
        setErrorMessage(undefined);
        const toastId = toast.loading(`Deleting ${objectName}…`);
        try {
            await deleteAsset(props.asset.id).unwrap();
            toast.update(toastId, { type: 'success', render: `${objectName} deleted`, isLoading: false, autoClose: 3000 });
            props.onDismiss();
        } catch (e) {
            // 409 Conflict when the asset is still referenced; the API explains where.
            const detail = extractErrorMessage(e);
            setErrorMessage(detail ?? 'Failed to delete asset');
            toast.update(toastId, {
                type: 'error',
                render: `Failed to delete ${objectName}${detail ? `: ${detail}` : ''}`,
                isLoading: false,
                autoClose: 4000,
            });
        }
        setBusy(false);
    };

    const usageCountText = `${usages.length} place${usages.length === 1 ? '' : 's'}`;

    interface IConfirmContent {
        title: string;
        subText: string;
        action: () => void;
        actionText: string;
        danger?: boolean;
        actionDisabled?: boolean;
        checking?: boolean;
    }

    const confirmContent = (): IConfirmContent | undefined => {
        switch (confirm) {
            case 'promote':
                return {
                    title: 'Promote to Global',
                    subText: `Make ${objectName} available to every storefront in this organization? Existing references keep working.`,
                    action: promote,
                    actionText: 'Promote',
                };
            case 'replace':
                return {
                    title: 'Replace Image',
                    subText: 'This updates every place the image is used, including live storefronts.',
                    action: () => {
                        setConfirm(undefined);
                        replaceInputRef.current?.click();
                    },
                    actionText: 'Choose File',
                };
            case 'delete':
                // Delete can be reached before the usage lookup has run, so the dialog does the
                // guard: it waits for the answer, then either blocks or confirms.
                if (usage.isError) {
                    return {
                        title: 'Delete Asset',
                        subText: 'Could not check where this asset is used, so it cannot be deleted safely right now. Close and try again.',
                        action: () => setConfirm(undefined),
                        actionText: 'Delete',
                        actionDisabled: true,
                    };
                }
                if (!usageKnown) {
                    return {
                        title: 'Delete Asset',
                        subText: 'Checking where this asset is used…',
                        action: remove,
                        actionText: 'Delete',
                        actionDisabled: true,
                        checking: true,
                    };
                }
                if (usages.length > 0) {
                    return {
                        title: 'Delete Asset',
                        subText: `${objectName} is still used in ${usageCountText}. Remove those references first; the References tab lists them.`,
                        action: () => setConfirm(undefined),
                        actionText: 'Delete',
                        actionDisabled: true,
                    };
                }
                return {
                    title: 'Delete Asset',
                    subText: `Permanently delete ${objectName}? This removes the file from storage.`,
                    action: remove,
                    actionText: 'Delete',
                    danger: true,
                };
            default:
                return undefined;
        }
    };

    const dialog = confirmContent();
    const deleteBlocked = usageKnown && usages.length > 0;

    const rowShell: React.CSSProperties = {
        padding: 6,
        border: `1px solid ${theme.palette.neutralLight}`,
        borderRadius: 4,
        background: theme.palette.white,
    };

    const emptyBox: React.CSSProperties = {
        padding: '1em',
        border: `1px dashed ${theme.palette.neutralTertiaryAlt}`,
        borderRadius: 4,
        fontSize: 12,
        color: theme.palette.neutralSecondary,
    };

    const detailRow = (label: string, value: React.ReactNode) => (
        <Stack horizontal tokens={{ childrenGap: '0.5em' }} style={{ padding: '2px 0' }}>
            <span style={{ width: 110, flexShrink: 0, fontSize: 12, color: theme.palette.neutralSecondary }}>{label}</span>
            <span style={{ fontSize: 12, wordBreak: 'break-word' }}>{value}</span>
        </Stack>
    );

    const usageRow = (u: IStorefrontAssetUsage, i: number) => {
        const locationLabel = usageLocationLabel(u.location);
        const storefrontLabel = u.storefront
            ? (u.storefront === STOREFRONT_ASSET_GLOBAL ? 'Global' : storefrontNames[u.storefront])
            : undefined;
        const subtitle = [locationLabel, storefrontLabel, u.field].filter(Boolean).join(' · ');
        return (
            <div key={`${u.location}-${u.documentId}-${u.field ?? ''}-${i}`} style={rowShell}>
                <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 8 }}>
                    <Stack grow style={{ minWidth: 0 }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{u.documentName || locationLabel}</span>
                        <span style={{ fontSize: 11, color: theme.palette.neutralSecondary }}>{subtitle}</span>
                    </Stack>
                    {u.storefront === STOREFRONT_ASSET_GLOBAL && <ScopePill label='Global' global={true} />}
                </Stack>
            </div>
        );
    };

    // One Stack, not two array entries: PanelWrapper spreads separate entries to
    // opposite edges of the footer.
    const footerButtons = [
        <Stack key='asset-footer' horizontal verticalAlign='center' tokens={{ childrenGap: '0.5em' }}>
            <PrimaryButton
                text='Save'
                iconProps={{ iconName: 'Save' }}
                disabled={saving || busy || !props.asset || !dirty}
                onClick={save}
            />
            <DefaultButton text='Close' disabled={saving} onClick={props.onDismiss} />
        </Stack>,
    ];

    return (
        <PanelWrapper
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            title='Asset'
            type={PanelType.medium}
            isFooterAtBottom={true}
            footerButtons={footerButtons}
        >
            <input
                type='file'
                ref={replaceInputRef}
                style={{ display: 'none' }}
                onChange={onReplaceFile}
                accept='.gif,.png,.jpg,.jpeg,.webp,.svg,image/svg+xml'
            />
            {!props.asset && <MessageBar>No asset selected.</MessageBar>}
            {props.asset && (
                <>
                    {errorMessage && (
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            onDismiss={() => setErrorMessage(undefined)}
                            styles={{ root: { marginTop: '1em' } }}
                        >
                            {errorMessage}
                        </MessageBar>
                    )}
                    <Pivot
                        overflowBehavior='menu'
                        selectedKey={selectedTab}
                        onLinkClick={(item) => openTab((item?.props.itemKey as TabKey) ?? 'details')}
                        styles={{ itemContainer: { marginTop: '1em' } }}
                    >
                        <PivotItem headerText='Details' itemKey='details'>
                            <Stack tokens={{ childrenGap: '1em' }}>
                                <div style={{ ...SETTINGS_BOX, padding: 8 }}>
                                    <div style={{
                                        height: 160,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative',
                                    }}>
                                        <Image
                                            src={props.asset.url}
                                            alt={props.asset.name}
                                            height={160}
                                            imageFit={ImageFit.contain}
                                        />
                                        {busy && (
                                            <div style={{
                                                position: 'absolute',
                                                top: 0, left: 0, right: 0, bottom: 0,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                backgroundColor: 'rgba(255, 255, 255, 0.75)',
                                            }}>
                                                <Spinner size={SpinnerSize.large} />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <FormSectionHeader label='Identity' />
                                    <Stack tokens={{ childrenGap: '0.5em' }}>
                                        <TextField
                                            label='Name'
                                            value={name}
                                            onChange={(_, v) => setName(v ?? '')}
                                        />
                                        <AssetTagInput tags={tags} onChange={setTags} disabled={busy} />
                                    </Stack>
                                </div>

                                <div>
                                    <FormSectionHeader label='Scope' />
                                    <div style={SETTINGS_BOX}>
                                        <Stack horizontal verticalAlign='center' tokens={{ childrenGap: '1em' }}>
                                            <Stack grow style={{ minWidth: 0 }}>
                                                <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 6 }}>
                                                    <span style={{ fontWeight: 600, fontSize: 13 }}>{scopeLabel}</span>
                                                    <ScopePill label={isGlobal ? 'Global' : 'Storefront'} global={isGlobal} />
                                                </Stack>
                                                <span style={{ ...HELPER_TEXT, marginTop: 4 }}>
                                                    {isGlobal
                                                        ? 'Every storefront in this organization can use this asset.'
                                                        : 'Only this storefront can use the asset. Promote it to share it org-wide.'}
                                                </span>
                                            </Stack>
                                            {!isGlobal && (
                                                <DefaultButton
                                                    text='Promote to Global'
                                                    iconProps={{ iconName: 'Up' }}
                                                    disabled={busy}
                                                    onClick={() => setConfirm('promote')}
                                                />
                                            )}
                                        </Stack>
                                    </div>
                                </div>

                                <div>
                                    <FormSectionHeader label='Image File' />
                                    <div style={SETTINGS_BOX}>
                                        <Stack horizontal wrap tokens={{ childrenGap: '0.5em' }}>
                                            <DefaultButton
                                                text='Replace Image'
                                                iconProps={{ iconName: 'Refresh' }}
                                                disabled={busy}
                                                onClick={() => setConfirm('replace')}
                                            />
                                            <TooltipHost
                                                content={deleteBlocked
                                                    ? `In use in ${usageCountText}. Remove those references first.`
                                                    : undefined}
                                            >
                                                {/* Plain here, red only on the confirm: btn-warning
                                                    keeps its red background when disabled, and this
                                                    button spends most of its life disabled. */}
                                                <DefaultButton
                                                    text='Delete'
                                                    iconProps={{ iconName: 'Delete' }}
                                                    disabled={busy || deleteBlocked}
                                                    onClick={() => { setUsageRequested(true); setConfirm('delete'); }}
                                                />
                                            </TooltipHost>
                                        </Stack>
                                        <div style={{ ...HELPER_TEXT, marginTop: '0.5em' }}>
                                            Replacing swaps the file behind this asset everywhere it is used, including live storefronts.
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <FormSectionHeader label='Details' />
                                    <div style={SETTINGS_BOX}>
                                        {detailRow('Source', assetSourceLabel(props.asset.source))}
                                        {detailRow('Size', formatBytes(props.asset.sizeBytes))}
                                        {detailRow('Type', props.asset.contentType)}
                                        {detailRow('File', blobPathTail(props.asset.blobPath))}
                                        {detailRow('Updated', props.asset.lastUpdated
                                            ? `${new Date(props.asset.lastUpdated).toLocaleString()}${props.asset.username ? ` by ${props.asset.username}` : ''}`
                                            : 'Not recorded')}
                                    </div>
                                </div>
                            </Stack>
                        </PivotItem>

                        <PivotItem
                            headerText='References'
                            itemKey='references'
                            itemCount={usageKnown ? usages.length : undefined}
                        >
                            <Stack tokens={{ childrenGap: '0.5em' }}>
                                <div style={HELPER_TEXT}>
                                    Every storefront setting, banner, section, style, and template that points at this image.
                                </div>
                                {(!usageKnown && (usage.isFetching || usage.isUninitialized)) && (
                                    <Spinner size={SpinnerSize.small} label='Checking references' labelPosition='right' />
                                )}
                                {usage.isError && (
                                    <MessageBar messageBarType={MessageBarType.error}>Failed to load references.</MessageBar>
                                )}
                                {usageKnown && usages.length === 0 && (
                                    <div style={emptyBox}>Not used anywhere yet.</div>
                                )}
                                {usageKnown && usages.length > 0 && (
                                    <Stack tokens={{ childrenGap: 8 }}>
                                        {usages.map(usageRow)}
                                    </Stack>
                                )}
                            </Stack>
                        </PivotItem>

                        {/* Fluent's Pivot only mounts the selected item, so the activity query does
                            not run until this tab is opened. */}
                        <PivotItem headerText='Activity' itemKey='activity'>
                            <AssetActivityList assetId={props.asset.id} />
                        </PivotItem>
                    </Pivot>
                </>
            )}

            <Dialog
                hidden={!dialog}
                onDismiss={() => setConfirm(undefined)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: dialog?.title,
                    subText: dialog?.subText,
                }}
                modalProps={{ isBlocking: true }}
            >
                {dialog?.checking && <Spinner size={SpinnerSize.small} label='Loading references' labelPosition='right' />}
                <DialogFooter>
                    {dialog?.danger
                        ? (
                            <DefaultButton
                                className='btn-warning'
                                text={dialog.actionText}
                                disabled={dialog.actionDisabled}
                                onClick={() => dialog.action()}
                            />
                        )
                        : (
                            <PrimaryButton
                                text={dialog?.actionText}
                                disabled={dialog?.actionDisabled}
                                onClick={() => dialog?.action()}
                            />
                        )}
                    <DefaultButton text='Cancel' onClick={() => setConfirm(undefined)} />
                </DialogFooter>
            </Dialog>
        </PanelWrapper>
    );
};

export default AssetDetailsPanel;
